import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {WarningTwoOptionDialogModel} from './model/warning-two-option-dialog.model';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {getLanguageEnumByLanguageCode} from '@core/enums/language.enum';
import {ReadingSpeedEnum} from '@core/enums/reading-speed.enum';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';

@UntilDestroy()
@Component({
    selector: 'app-start-new-consent-popup',
    templateUrl: './start-new-consent-popup.component.html',
    styleUrls: ['./start-new-consent-popup.component.scss']
})
export class StartNewConsentPopupComponent implements OnInit, OnDestroy {
    @Output() closeDialogEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() confirmActionEvent = new EventEmitter();

    dialogData: WarningTwoOptionDialogModel;

    audio = new Audio();
    firstPlay = true;
    ttsUrl: string | undefined;
    repeatButtonActive = false;
    ttsIconState = TtsControlEnum.playState;

    constructor(@Inject(MAT_DIALOG_DATA) data: WarningTwoOptionDialogModel,
                private ttsHelperService: TtsHelperService, private ttsService: TtsRestService) {
        this.dialogData = data;
    }

    ngOnInit(): void {
        this.audio.addEventListener('ended', this.setButtonOnEnd, false);
        if (sessionStorage.getItem('audioMode')) {
            this.readText();
        }
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.playAudio();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resumeAudio();
                } else {
                    this.pauseAudio();
                }
            }
        }
    }

    convertTextToSpeech(): void {
        const removedHtmlTagsTitle = this.ttsHelperService.removeHTMLTags(this.dialogData.title);
        const removedHtmlTagsInfo = this.ttsHelperService.removeSSML(this.dialogData.content);
        const finalText = removedHtmlTagsTitle.concat('? ').concat(removedHtmlTagsInfo);

        const pollyObject = {
            text: finalText,
            readingSpeed: ReadingSpeedEnum.MEDIUM,
            readingLanguage: getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')),
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonActive = true;
        this.ttsService.getTTS(pollyObject).pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.playAudio();
            }
        });
    }

    playAudio(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    pauseAudio(): void {
        this.ttsIconState = TtsControlEnum.playState;
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    resumeAudio(): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            } else {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    setButtonOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    onCloseDialog(): void {
        this.pauseAudio();
        this.closeDialogEvent.emit();
    }

    onConfirmAction(): void {
        this.pauseAudio();
        this.confirmActionEvent.emit();
    }

    ngOnDestroy(): void {
        this.pauseAudio();
    }
}
